import React, { useMemo, useState } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const BulkCommunicationPanel = ({ 
  selectedEmployees = [], 
  onClose, 
  onSendMessage,
  allEmployees = []
}) => {
  const { t, language } = useTranslation(['employeeDirectory', 'common']);
  const [messageType, setMessageType] = useState('announcement');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [includeEmail, setIncludeEmail] = useState(true);
  const [includeSMS, setIncludeSMS] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');

  const messageTypeOptions = useMemo(() => [
    { value: 'announcement', label: t('employeeDirectory:bulkPanel.types.announcement', 'General Announcement') },
    { value: 'schedule', label: t('employeeDirectory:bulkPanel.types.schedule', 'Schedule Update') },
    { value: 'emergency', label: t('employeeDirectory:bulkPanel.types.emergency', 'Emergency Alert') },
    { value: 'policy', label: t('employeeDirectory:bulkPanel.types.policy', 'Policy Update') },
    { value: 'training', label: t('employeeDirectory:bulkPanel.types.training', 'Training Notification') }
  ], [t]);

  const priorityOptions = useMemo(() => [
    { value: 'low', label: t('employeeDirectory:bulkPanel.priority.low', 'Low Priority') },
    { value: 'normal', label: t('employeeDirectory:bulkPanel.priority.normal', 'Normal Priority') },
    { value: 'high', label: t('employeeDirectory:bulkPanel.priority.high', 'High Priority') },
    { value: 'urgent', label: t('employeeDirectory:bulkPanel.priority.urgent', 'Urgent') }
  ], [t]);

  const handleSend = () => {
    const messageData = {
      type: messageType,
      subject,
      message,
      priority,
      recipients: selectedEmployees,
      channels: {
        email: includeEmail,
        sms: includeSMS
      },
      scheduled: scheduleMessage ? scheduledTime : null
    };
    
    onSendMessage(messageData);
    onClose();
  };

  const getSelectedEmployeeNames = () => {
    return selectedEmployees
      ?.map((id) => allEmployees?.find((emp) => emp?.id === id)?.name)
      ?.filter(Boolean)
      ?.join(', ');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'text-muted-foreground';
      case 'normal': return 'text-primary';
      case 'high': return 'text-warning';
      case 'urgent': return 'text-error';
      default: return 'text-primary';
    }
  };

  const selectedCountLabel = formatTranslation(
    t(
      `employeeDirectory:bulkPanel.selected.${selectedEmployees?.length === 1 ? 'singular' : 'plural'}`,
      selectedEmployees?.length === 1
        ? '{{count}} employee selected'
        : '{{count}} employees selected',
    ),
    { count: selectedEmployees?.length ?? 0 },
  );

  const subtitle = formatTranslation(
    t(
      `employeeDirectory:bulkPanel.subtitle.${selectedEmployees?.length === 1 ? 'singular' : 'plural'}`,
      selectedEmployees?.length === 1
        ? 'Send a message to {{count}} selected employee'
        : 'Send a message to {{count}} selected employees',
    ),
    { count: selectedEmployees?.length ?? 0 },
  );

  const priorityChipLabel = (() => {
    switch (priority) {
      case 'low':
        return t('employeeDirectory:bulkPanel.priorityChip.low', 'Low Priority');
      case 'high':
        return t('employeeDirectory:bulkPanel.priorityChip.high', 'High Priority');
      case 'urgent':
        return t('employeeDirectory:bulkPanel.priorityChip.urgent', 'Urgent');
      default:
        return t('employeeDirectory:bulkPanel.priorityChip.normal', 'Normal Priority');
    }
  })();

  const viaLabel = (() => {
    const channels = [
      includeEmail && t('employeeDirectory:bulkPanel.via.email', 'Email'),
      includeSMS && t('employeeDirectory:bulkPanel.via.sms', 'SMS'),
    ]
      ?.filter(Boolean)
      ?.join(', ');
    return channels || t('employeeDirectory:bulkPanel.via.none', 'No channels selected');
  })();

  const scheduledLabel = scheduledTime
    ? formatTranslation(
        t('employeeDirectory:bulkPanel.schedule.label', 'Scheduled: {{time}}'),
        {
          time: new Date(scheduledTime)?.toLocaleString(language || undefined),
        },
      )
    : '';

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {t('employeeDirectory:bulkPanel.title', 'Bulk Communication')}
            </h2>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
          {/* Recipients */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              {t('employeeDirectory:bulkPanel.recipients', 'Recipients')}
            </label>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-foreground">{selectedCountLabel}</p>
              <p className="text-xs text-muted-foreground mt-1 truncate">
                {getSelectedEmployeeNames()}
              </p>
            </div>
          </div>

          {/* Message Type and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={t('employeeDirectory:bulkPanel.fields.messageType', 'Message Type')}
              options={messageTypeOptions}
              value={messageType}
              onChange={setMessageType}
            />
            
            <Select
              label={t('employeeDirectory:bulkPanel.fields.priority', 'Priority')}
              options={priorityOptions}
              value={priority}
              onChange={setPriority}
            />
          </div>

          {/* Subject */}
          <Input
            label={t('employeeDirectory:bulkPanel.fields.subject', 'Subject')}
            type="text"
            placeholder={t('employeeDirectory:bulkPanel.placeholders.subject', 'Enter message subject...')}
            value={subject}
            onChange={(e) => setSubject(e?.target?.value)}
            required
          />

          {/* Message */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              {t('employeeDirectory:bulkPanel.fields.message', 'Message')}
            </label>
            <textarea
              className="w-full h-32 p-3 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={t('employeeDirectory:bulkPanel.placeholders.message', 'Type your message here...')}
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              {formatTranslation(
                t('employeeDirectory:bulkPanel.characterCount', '{{count}}/500 characters'),
                { count: message?.length ?? 0 },
              )}
            </p>
          </div>

          {/* Delivery Channels */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              {t('employeeDirectory:bulkPanel.delivery.title', 'Delivery Channels')}
            </label>
            <div className="space-y-3">
              <Checkbox
                label={t('employeeDirectory:bulkPanel.delivery.email', 'Send via Email')}
                description={t('employeeDirectory:bulkPanel.delivery.emailDescription', 'Deliver message to employee email addresses')}
                checked={includeEmail}
                onChange={(e) => setIncludeEmail(e?.target?.checked)}
              />
              <Checkbox
                label={t('employeeDirectory:bulkPanel.delivery.sms', 'Send via SMS')}
                description={t('employeeDirectory:bulkPanel.delivery.smsDescription', 'Send text message to employee mobile numbers')}
                checked={includeSMS}
                onChange={(e) => setIncludeSMS(e?.target?.checked)}
              />
            </div>
          </div>

          {/* Schedule Message */}
          <div>
            <Checkbox
              label={t('employeeDirectory:bulkPanel.schedule.title', 'Schedule Message')}
              description={t('employeeDirectory:bulkPanel.schedule.description', 'Send message at a specific time')}
              checked={scheduleMessage}
              onChange={(e) => setScheduleMessage(e?.target?.checked)}
            />
            
            {scheduleMessage && (
              <div className="mt-3">
                <Input
                  label={t('employeeDirectory:bulkPanel.schedule.timeLabel', 'Scheduled Time')}
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e?.target?.value)}
                  required
                />
              </div>
            )}
          </div>

          {/* Message Preview */}
          <div className="border border-border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground">
                {t('employeeDirectory:bulkPanel.preview.title', 'Message Preview')}
              </h4>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${getPriorityColor(priority)} bg-current/10`}>
                {priorityChipLabel}
              </span>
            </div>
            <div className="space-y-2">
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  {t('employeeDirectory:bulkPanel.preview.subject', 'Subject:')}
                </span>
                <span className="text-sm text-foreground ml-1">
                  {subject || t('employeeDirectory:bulkPanel.preview.noSubject', 'No subject')}
                </span>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground">
                  {t('employeeDirectory:bulkPanel.preview.message', 'Message:')}
                </span>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {message || t('employeeDirectory:bulkPanel.preview.noMessage', 'No message content')}
                </p>
              </div>
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span>
                  {formatTranslation(
                    t('employeeDirectory:bulkPanel.via.label', 'Via: {{channels}}'),
                    { channels: viaLabel },
                  )}
                </span>
                {scheduleMessage && scheduledTime && <span>{scheduledLabel}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {formatTranslation(
              t(
                `employeeDirectory:bulkPanel.recipientsFooter.${selectedEmployees?.length === 1 ? 'singular' : 'plural'}`,
                selectedEmployees?.length === 1 ? '{{count}} recipient' : '{{count}} recipients',
              ),
              { count: selectedEmployees?.length ?? 0 },
            )}
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              {t('employeeDirectory:bulkPanel.actions.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleSend}
              disabled={!subject?.trim() || !message?.trim() || (!includeEmail && !includeSMS)}
            >
              <Icon name="Send" size={16} className="mr-2" />
              {scheduleMessage
                ? t('employeeDirectory:bulkPanel.actions.schedule', 'Schedule Message')
                : t('employeeDirectory:bulkPanel.actions.send', 'Send Message')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkCommunicationPanel;
